// v3.18-1326 — Offer Stacking action bar.
//
// A thin horizontal panel that sits between the Financing Offers section
// and the Lender Portal section on the deal dashboard. When "Stack Offers"
// is UNCHECKED the panel is inert — existing single-offer selection
// workflows behave exactly as before.
//
// When "Stack Offers" is CHECKED the operator may select multiple
// financing offers simultaneously. The panel displays live combined
// totals (Approved Amount, Monthly Payment) and — for authenticated
// internal staff only — an Internal Average Interest Rate.  The
// "Send Combined Offers to Agent" button opens the same delivery
// pipeline as the existing single-offer send, but the outbound package
// is strictly lender-neutral (only combined totals + offer count).
import { useEffect, useMemo, useRef, useState } from 'react';
import useLiveActivityClaim from '../hooks/useLiveActivityClaim';   // v3.18-1343 LAC-VERIFY
import { Card, CardContent } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Layers, Send, ShieldCheck, AlertTriangle, Check, Mail, Save } from 'lucide-react';
import SendCombinedOffersToAgentModal from './SendCombinedOffersToAgentModal';
import api from '../lib/api';

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(v));
};

const fmtPct = (v) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(2)}%`;
};

// v3.18-1327 — Status badge palette. The stack status is a first-class
// property of the Deal so operators can see the financing state at a
// glance without opening the details.
const STATUS_DISPLAY = {
  no_stack:         { label: 'No Financing Stack',           tone: 'slate',   Icon: Layers        },
  saved:            { label: 'Financing Stack Saved',        tone: 'blue',    Icon: Save          },
  sent_to_agent:    { label: 'Financing Stack Sent to Agent', tone: 'amber',   Icon: Mail          },
  accepted:         { label: 'Financing Stack Accepted',     tone: 'emerald', Icon: Check         },
  requires_review:  { label: 'Financing Stack Requires Review', tone: 'red',  Icon: AlertTriangle },
};
const TONE_CLASS = {
  slate:   'bg-slate-100 text-slate-700 border-slate-300',
  blue:    'bg-blue-50 text-blue-700 border-blue-300',
  amber:   'bg-amber-50 text-amber-800 border-amber-300',
  emerald: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  red:     'bg-red-50 text-red-700 border-red-300',
};

/**
 * @param {Object} props
 * @param {Object} props.client
 * @param {boolean} props.isInternal   Only internal staff see the Internal
 *                                     Average Interest Rate and lender IDs.
 */
export default function OfferStackingBar({ client, isInternal }) {
  const [stackOn, setStackOn] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [sendOpen, setSendOpen] = useState(false);

  // v3.18-1343 LAC-VERIFY — claim "creating financing stack" while
  // the operator is actively assembling a package (i.e. stackOn is
  // true).  When the operator toggles OFF or navigates away, the
  // hook releases the claim; ClientLiveActivityStrip on peers'
  // browsers loses the row within one heartbeat cycle.
  useLiveActivityClaim({
    clientId: client?.id,
    workflow: 'creating_financing_stack',
    enabled:  !!(stackOn && client?.id),
  });

  // v3.18-1327 — Deal Persistence.  Hydrate from ``client.offer_stack``
  // on mount / client change so a saved stack is auto-restored when the
  // Deal is reopened.  A debounced auto-save PUT propagates every
  // subsequent change (add / remove / change / disable) back to the
  // Deal — the operator never has to press a "Save Stack" button.
  const hydratedRef  = useRef(false);
  const clientIdRef  = useRef(null);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    const cid = client?.id || null;
    // Reset hydration guard whenever the underlying deal changes so the
    // next incoming ``client.offer_stack`` snapshot is applied cleanly.
    if (clientIdRef.current !== cid) {
      hydratedRef.current = false;
      clientIdRef.current = cid;
    }
    if (hydratedRef.current) return;
    const s = client?.offer_stack;
    if (s && typeof s === 'object') {
      setStackOn(!!s.enabled);
      setSelected(new Set(Array.isArray(s.offer_ids) ? s.offer_ids : []));
    } else {
      setStackOn(false);
      setSelected(new Set());
    }
    hydratedRef.current = true;
  }, [client?.id, client?.offer_stack]);

  // Assemble each offer as a plain object. Lender info flows into the
  // internal audit ONLY; it is NEVER surfaced in the outbound email body.
  const offer1 = useMemo(() => ({
    offerNumber: 1,
    approved_amount: client?.approved_amount,
    payment:         client?.payment || client?.monthly_payment,
    rate:            client?.rate    || client?.apr,
    term:            client?.term,
    lender_name:     client?.lender_name || null,
    lender_id:       client?.lender_id || null,
  }), [client]);

  const offer2 = useMemo(() => {
    const o = client?.financing_option_2 || {};
    return {
      offerNumber: 2,
      approved_amount: o.approved_amount,
      payment:         o.payment,
      rate:            o.rate,
      term:            o.term,
      lender_name:     o.lender_name || null,
      lender_id:       o.lender_id || null,
    };
  }, [client]);

  const availableOffers = useMemo(() => {
    const list = [];
    if (offer1.approved_amount) list.push(offer1);
    if (offer2.approved_amount) list.push(offer2);
    return list;
  }, [offer1, offer2]);

  // v3.18-1329 — Offers derived STRICTLY from the persisted saved
  // stack (``client.offer_stack.offer_ids``).  This is the ONLY input
  // the Send Stacked Package modal is allowed to consume — never the
  // in-memory ``selected`` set.  If auto-save is still in-flight the
  // caller must flush the debounce before opening the modal (see
  // ``handleSendClick`` below).
  const savedOfferIds = useMemo(
    () => Array.isArray(client?.offer_stack?.offer_ids)
      ? client.offer_stack.offer_ids
      : [],
    [client?.offer_stack?.offer_ids],
  );
  const savedStackOffers = useMemo(() => {
    const map = { 1: offer1, 2: offer2 };
    return savedOfferIds
      .map(id => map[id])
      .filter(o => o && o.approved_amount);
  }, [savedOfferIds, offer1, offer2]);
  const hasSavedStack = !!client?.offer_stack?.enabled
    && savedStackOffers.length >= 2;

  const toggleOne = (n) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  };

  const selectedOffers = useMemo(
    () => availableOffers.filter(o => selected.has(o.offerNumber)),
    [availableOffers, selected]
  );

  const combined = useMemo(() => {
    const approved = selectedOffers.reduce((a, o) => a + (Number(o.approved_amount) || 0), 0);
    const payment  = selectedOffers.reduce((a, o) => a + (Number(o.payment) || 0), 0);
    const rates    = selectedOffers.map(o => Number(o.rate)).filter(r => Number.isFinite(r) && r > 0);
    const avgRate  = rates.length ? rates.reduce((a, r) => a + r, 0) / rates.length : null;
    return { approved, payment, avgRate, count: selectedOffers.length };
  }, [selectedOffers]);

  // v3.18-1327 — debounced auto-persist. Skips the initial hydration
  // render (no round-trip on load) and coalesces rapid clicks into a
  // single PUT so an operator can toggle several offers without
  // hammering the API.  Every terminal state IS saved — even the
  // "disabled + empty" state — so the audit trail is complete.
  useEffect(() => {
    if (!hydratedRef.current || !client?.id) return;
    const payload = {
      enabled:                    stackOn,
      offer_ids:                  Array.from(selected).sort(),
      combined_approved_amount:   Math.round(combined.approved * 100) / 100,
      combined_monthly_payment:   Math.round(combined.payment * 100) / 100,
      internal_avg_interest_rate: combined.avgRate !== null
        ? Math.round(combined.avgRate * 100) / 100
        : null,
    };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      api.put(`/clients/${client.id}/offer-stack`, payload).catch(() => {
        // Soft-fail — the operator sees no interruption; the next
        // successful save will heal the drift.  Never toast here, this
        // fires on every micro-change.
      });
    }, 500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [client?.id, stackOn, selected, combined.approved, combined.payment, combined.avgRate]);

  // v3.18-1327 — Status derived from ``client.offer_stack.status`` if
  // present; otherwise inferred from local state.
  const savedStatus       = client?.offer_stack?.status || null;
  const invalidationRsn   = client?.offer_stack?.invalidation_reason || null;
  const displayStatus = useMemo(() => {
    if (savedStatus) return savedStatus;
    if (stackOn && combined.count >= 1) return 'saved';
    return 'no_stack';
  }, [savedStatus, stackOn, combined.count]);
  const statusMeta = STATUS_DISPLAY[displayStatus] || STATUS_DISPLAY.no_stack;
  const StatusIcon = statusMeta.Icon;

  // Reset selection when stacking is disabled — but do NOT wipe local
  // memory; the saved stack on the Deal is the source of truth. If the
  // operator re-enables Stack Offers, the hydration effect above will
  // repopulate ``selected`` from the persisted stack.
  const handleToggleStack = (checked) => {
    setStackOn(!!checked);
    if (!checked) setSelected(new Set());
  };

  // Requires Review gates the send. The operator MUST re-save (which
  // resets status → saved) before a new outbound is allowed.
  const requiresReview = displayStatus === 'requires_review';
  // v3.18-1329 — The Send Stacked Package action reads STRICTLY from
  // the persisted ``client.offer_stack``.  In-memory ``selected`` is
  // not consulted here.  If no saved stack exists (or it is disabled /
  // has fewer than two offers) the button is disabled and an inline
  // "No saved financing stack exists." message is shown.
  const canSend = hasSavedStack && !requiresReview;

  // v3.18-1329 — Flush the pending debounced auto-save before opening
  // the modal so the saved stack on the server matches what the
  // operator sees on screen. If auto-save is in-flight we PUT
  // synchronously; if the operator's UI selection differs from the
  // saved stack (e.g. because they clicked Send within the debounce
  // window), we save the current selection first and then open the
  // modal.  The modal itself reads exclusively from the freshly-saved
  // ``client.offer_stack``.
  const handleOpenSendModal = async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (client?.id) {
      const localIds = Array.from(selected).sort();
      const remoteIds = [...savedOfferIds].sort();
      const idsChanged  = JSON.stringify(localIds) !== JSON.stringify(remoteIds);
      const stateChanged = (!!client?.offer_stack?.enabled) !== !!stackOn;
      if (idsChanged || stateChanged) {
        try {
          await api.put(`/clients/${client.id}/offer-stack`, {
            enabled:  stackOn,
            offer_ids: localIds,
            combined_approved_amount: Math.round(combined.approved * 100) / 100,
            combined_monthly_payment: Math.round(combined.payment * 100) / 100,
            internal_avg_interest_rate: combined.avgRate !== null
              ? Math.round(combined.avgRate * 100) / 100
              : null,
          });
        } catch (_e) {
          // Non-fatal — the modal's own guard will surface "No saved
          // financing stack exists." if the save didn't land.
        }
      }
    }
    setSendOpen(true);
  };

  return (
    <>
      <Card
        className="border border-indigo-200 bg-gradient-to-r from-indigo-50/30 to-slate-50/30"
        data-testid="offer-stacking-bar"
      >
        <CardContent className="px-4 py-3 space-y-3">
          {/* Header row */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-indigo-500" />
              <h3 className="text-sm font-semibold text-slate-700">Offer Stacking</h3>
              {/* v3.18-1327 — Financing Stack Status badge.  Always
                  visible (even when Stack Offers is off) so operators
                  know the state at a glance. */}
              <span
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${TONE_CLASS[statusMeta.tone]}`}
                data-testid="offer-stacking-status-badge"
                data-status={displayStatus}
                title={invalidationRsn || undefined}
              >
                <StatusIcon className="h-3 w-3" />
                {statusMeta.label}
              </span>
              {stackOn && (
                <span
                  className="text-[10px] uppercase tracking-wider rounded bg-indigo-100 text-indigo-700 px-1.5 py-0.5"
                  data-testid="offer-stacking-status"
                >
                  {combined.count} {combined.count === 1 ? 'Offer' : 'Offers'} Selected
                </span>
              )}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={stackOn}
                onCheckedChange={handleToggleStack}
                data-testid="stack-offers-checkbox"
              />
              <span className="text-xs font-medium text-slate-700">Stack Offers</span>
            </label>
          </div>

          {/* v3.18-1327 — Requires Review banner. Blocks send until the
              operator acknowledges by re-saving the stack. */}
          {requiresReview && (
            <div
              className="flex items-start gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800"
              data-testid="offer-stacking-requires-review-banner"
            >
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <div className="space-y-0.5">
                <div className="font-semibold">Financing Stack Requires Review</div>
                <div>
                  An underlying offer has changed since this stack was saved. Review the
                  selection and re-save before sending again.
                  {invalidationRsn && (
                    <span className="block mt-0.5 text-red-700/80">Detected change: {invalidationRsn}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Body — only rendered when Stack Offers is ON */}
          {stackOn && (
            <div className="space-y-3">
              {availableOffers.length < 2 && (
                <p className="text-xs text-slate-500 italic" data-testid="offer-stacking-hint-need-two">
                  Add approved amounts on both offers to combine them.
                </p>
              )}
              {/* Per-offer selection row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {availableOffers.map(o => (
                  <label
                    key={o.offerNumber}
                    className={`flex items-center justify-between gap-3 rounded border px-3 py-2 cursor-pointer transition ${
                      selected.has(o.offerNumber)
                        ? 'border-indigo-400 bg-indigo-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                    data-testid={`offer-stacking-select-row-${o.offerNumber}`}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selected.has(o.offerNumber)}
                        onCheckedChange={() => toggleOne(o.offerNumber)}
                        data-testid={`offer-stacking-checkbox-${o.offerNumber}`}
                      />
                      <span className="text-xs font-medium text-slate-700">Offer {o.offerNumber}</span>
                    </div>
                    <div className="flex items-center gap-4 text-[11px] text-slate-500">
                      <span>Approved: <span className="text-slate-800 font-medium">{fmtMoney(o.approved_amount)}</span></span>
                      <span>Payment: <span className="text-slate-800 font-medium">{fmtMoney(o.payment)}</span></span>
                      <span>Rate: <span className="text-slate-800 font-medium">{fmtPct(o.rate)}</span></span>
                    </div>
                  </label>
                ))}
              </div>

              {/* Combined totals summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded bg-white/70 border border-indigo-200 px-3 py-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Approved</div>
                  <div className="text-sm font-semibold text-slate-800" data-testid="offer-stacking-total-approved">
                    {fmtMoney(combined.approved)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Monthly Payment</div>
                  <div className="text-sm font-semibold text-slate-800" data-testid="offer-stacking-total-payment">
                    {fmtMoney(combined.payment)}
                  </div>
                </div>
                {isInternal && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                      Internal Avg Rate
                      <span title="Internal only — never shown to agents or customers">
                        <ShieldCheck className="h-3 w-3 text-emerald-500" />
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-800" data-testid="offer-stacking-avg-rate">
                      {fmtPct(combined.avgRate)}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Selected Offers</div>
                  <div className="text-sm font-semibold text-slate-800" data-testid="offer-stacking-count">
                    {combined.count} {combined.count === 1 ? 'Offer' : 'Offers'} Selected
                  </div>
                </div>
              </div>

              {/* Send button */}
              <div className="flex justify-end items-center gap-3">
                {!hasSavedStack && (
                  <span
                    className="text-[11px] text-slate-500 italic"
                    data-testid="send-combined-no-saved-stack"
                  >
                    No saved financing stack exists.
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={handleOpenSendModal}
                  disabled={!canSend}
                  className="h-8 text-xs"
                  data-testid="send-combined-offers-btn"
                >
                  <Send className="h-3 w-3 mr-1" />
                  Send Stacked Package
                </Button>
              </div>
              {isInternal && (
                <p className="text-[10px] text-slate-400 italic" data-testid="offer-stacking-audit-notice">
                  Internal audit records every underlying financing source, provider IDs, and interest rates.
                  The outbound message contains only combined totals and the offer count.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <SendCombinedOffersToAgentModal
        open={sendOpen}
        onOpenChange={setSendOpen}
        client={client}
        offers={savedStackOffers}
        hasSavedStack={hasSavedStack}
      />
    </>
  );
}
